import type { ILayoutBlockPosterProps } from './LayoutBlockPoster.h'

import { useCallback, useMemo, useRef } from 'react'
import { FallbackImageSizes } from 'atoms/FallbackImage'
import { ImagePoster } from 'atoms/ImagePoster'
import { OverlayLayoutBanner, PosterContainerBanner } from 'blocks/Banner'
import { ContentStateBadge } from 'blocks/ContentStateBadge'
import { BannerSkeleton } from 'blocks/Skeletons'
import { useClientWidth, useOrientation } from 'utils/hooks'
import { BANNER_ASPECT_RATIO } from 'vars/banner-aspect-ratio'
import { BANNER_WIDTH } from 'vars/banner-width'
import { PosterOverlay, PosterOverlayDirection } from './LayoutBlockPoster.h'
import {
  DetailsBannerPosterSC,
  LayoutBlockPosterSC,
} from './LayoutBlockPoster.styled'

export const LayoutBlockPoster = ({
  overlayEnabled = true,
  overlayHeight,
  overlay = PosterOverlay.SURFACE_OPACITY_DARK_40,
  overlayHorizontal = PosterOverlay.BANNER_HORIZONTAL_OVERLAY,
  overlayVertical = PosterOverlay.BANNER_VERTICAL_OVERLAY,
  overlayWidth,
  overlayDirection = PosterOverlayDirection.OVERLAY_VERTICAL,
  posterUrl = '',
  posterMobileUrl = '',
  fallbackImageSize,
  width = BANNER_WIDTH.SIZE_100,
  aspectRatio = BANNER_ASPECT_RATIO.SIZE_10,
  loading = false,
  detailsBanner = false,
  stateBadge,
  blurPoster = false,
  ...rest
}: ILayoutBlockPosterProps) => {
  const Root = detailsBanner ? DetailsBannerPosterSC : LayoutBlockPosterSC
  const clientWidth = useClientWidth()
  const { orientation } = useOrientation()
  const layoutPosterRef = useRef<HTMLDivElement>(null)

  const isMobile = clientWidth < 600 && posterMobileUrl

  const mobileLandscape =
    clientWidth >= 600 &&
    clientWidth < 640 &&
    orientation === 'landscape' &&
    posterMobileUrl

  const poster = useMemo(
    () => (isMobile || mobileLandscape ? posterMobileUrl : posterUrl),
    [isMobile, mobileLandscape, posterMobileUrl, posterUrl]
  )

  const showsSkeleton = loading && !poster

  const renderPoster = useCallback(() => {
    if (showsSkeleton) {
      return clientWidth !== 0 ? (
        <BannerSkeleton aspectRatio={aspectRatio} bannerWidth={width} />
      ) : (
        <></>
      )
    }

    return clientWidth !== 0 ? (
      <PosterContainerBanner blurred={blurPoster}>
        <ImagePoster
          posterUrl={poster}
          alt='BannerImage'
          fallbackImageSize={
            fallbackImageSize ??
            (width === BANNER_WIDTH.SIZE_66
              ? FallbackImageSizes.M
              : FallbackImageSizes.XXL)
          }
          lazy={false}
        />
      </PosterContainerBanner>
    ) : (
      <></>
    )
  }, [
    clientWidth,
    poster,
    fallbackImageSize,
    width,
    showsSkeleton,
    aspectRatio,
    blurPoster,
  ])

  return (
    <Root
      ref={layoutPosterRef}
      width={width}
      aspectRatio={aspectRatio}
      {...rest}
    >
      {renderPoster()}
      {/* The scrims are shaped for artwork. Over the skeleton they paint it
          out, which reads as the banner blinking to black while the page
          loads, so they wait for the poster. */}
      <OverlayLayoutBanner
        overlay={overlay}
        overlayHorizontal={overlayHorizontal}
        overlayVertical={overlayVertical}
        overlayDirection={overlayDirection}
        overlayEnabled={overlayEnabled && !showsSkeleton}
        overlayWidth={overlayWidth}
        overlayHeight={overlayHeight}
      />
      {stateBadge ? <ContentStateBadge kind={stateBadge} /> : null}
    </Root>
  )
}
